import { CoilType, DuctType, FilterKind, type FullAirHandlerUnit } from "@eisox/api/core-api";

import { m } from "~/paraglide/messages";

type DeepKeys<T> = T extends readonly (infer U)[]
  ? DeepKeys<U>
  : T extends object
    ? { [K in keyof T & string]: K | DeepKeys<T[K]> }[keyof T & string]
    : never;

export const MAX_HISTORY_SELECTIONS = 6;

export type HistoryPointSelection = { entityId: string; point: string };

export const UNDISPLAYED_KEYS = [
  "id",
  "commandType",
  "stateNumber",
  "regType",
  "platesHeatExchanger",
  "floor",
  "lastBmsDataReport",
  "airHandlerUnitParentId",
  "group",
  "roomIds",
  "type",
  "kind",
  "coilType",
  "index",
  "eliminator",
  "heatingNetworkId",
  "hydraulicChangeover",
] as const satisfies readonly DeepKeys<FullAirHandlerUnit>[];

export const isUndisplayedKey = (key: string): boolean => (UNDISPLAYED_KEYS as readonly string[]).includes(key);

export const selectionKey = (entityId: string, point: string): string => `${entityId}:${point}`;

export const resolveEntityId = (value: unknown, parentEntityId: string): string => {
  if (
    value !== null &&
    typeof value === "object" &&
    "id" in value &&
    typeof value.id === "string" &&
    value.id.length > 0
  ) {
    return value.id;
  }
  return parentEntityId;
};

export const isPrimitiveArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.every(item => item === null || typeof item !== "object");

export const isNestedNode = (key: string, value: unknown): boolean => {
  if (isUndisplayedKey(key) || key === "name") return false;
  if (value === null || typeof value !== "object") return false;
  return !isPrimitiveArray(value);
};

export const isPointSelected = (selected: HistoryPointSelection[], entityId: string, point: string): boolean =>
  selected.some(item => item.entityId === entityId && item.point === point);

export const toggleSelection = (
  selected: HistoryPointSelection[],
  entityId: string,
  point: string,
  max = MAX_HISTORY_SELECTIONS,
): HistoryPointSelection[] => {
  if (isPointSelected(selected, entityId, point)) {
    return selected.filter(item => !(item.entityId === entityId && item.point === point));
  }
  if (selected.length >= max) return selected;
  return [...selected, { entityId, point }];
};

const collectDescendantEntityIds = (obj: unknown, entityId: string): Set<string> => {
  const ids = new Set<string>([entityId]);

  if (obj === null || typeof obj !== "object") return ids;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const itemEntityId = resolveEntityId(item, entityId);
      for (const id of collectDescendantEntityIds(item, itemEntityId)) ids.add(id);
    }
    return ids;
  }

  for (const [key, value] of Object.entries(obj)) {
    if (!isNestedNode(key, value)) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        const itemEntityId = resolveEntityId(item, entityId);
        for (const id of collectDescendantEntityIds(item, itemEntityId)) ids.add(id);
      }
    } else {
      const childEntityId = resolveEntityId(value, entityId);
      for (const id of collectDescendantEntityIds(value, childEntityId)) ids.add(id);
    }
  }

  return ids;
};

export const isBranchSelected = (
  obj: unknown,
  entityId: string,
  selected: HistoryPointSelection[],
  excludeSelf = false,
): boolean => {
  const entityIds = collectDescendantEntityIds(obj, entityId);
  if (excludeSelf) entityIds.delete(entityId);
  return selected.some(item => entityIds.has(item.entityId));
};

export const getKeyLabel = (key: string): string =>
  (m[`ahu_history_key_${key}` as keyof typeof m] as (() => string) | undefined)?.() ?? key;

const COIL_TITLE: Record<CoilType, () => string> = {
  [CoilType.HYDRAULIC_HEATING]: m.air_handler_unit_coil_hydraulic_heating,
  [CoilType.HYDRAULIC_COOLING]: m.air_handler_unit_coil_hydraulic_cooling,
  [CoilType.ELECTRIC]: m.air_handler_unit_coil_electric,
  [CoilType.HYDRAULIC_CHANGEOVER]: m.air_handler_unit_coil_hydraulic_changeover,
  [CoilType.CHILLER]: m.air_handler_unit_coil_chiller,
};

const DUCT_TYPE_TITLE: Record<DuctType, () => string> = {
  [DuctType.EXHAUST]: m.air_handler_unit_damper_exhaust,
  [DuctType.RETURN]: m.air_handler_unit_damper_return,
  [DuctType.SUPPLY]: m.air_handler_unit_damper_supply,
  [DuctType.OUTSIDE]: m.air_handler_unit_damper_outside,
};

// A duct carries several filters, so its own name does not tell them apart.
const FILTER_KIND_TITLE: Record<FilterKind, (() => string) | undefined> = {
  [FilterKind.MAIN]: undefined,
  [FilterKind.PRE]: m.air_handler_unit_filter_pre,
  [FilterKind.TERMINAL]: m.air_handler_unit_filter_terminal,
};

export const getFilterLabel = (type: DuctType, kind: FilterKind): string => {
  const suffix = FILTER_KIND_TITLE[kind]?.();
  return suffix ? `${DUCT_TYPE_TITLE[type]()} (${suffix})` : DUCT_TYPE_TITLE[type]();
};

export const remapObjectWithSortedKeys = (obj: object): Record<string, unknown> =>
  Object.entries(obj)
    .map(([key, value]) => ({
      key,
      translation: getKeyLabel(key),
      value,
    }))
    .sort((a, b) => a.translation.localeCompare(b.translation))
    .reduce(
      (acc, { key, value }) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<string, unknown>,
    );

export const getNodeLabel = (key: string, value: unknown, parentKey?: string): string => {
  if (value !== null && typeof value === "object" && "name" in value && typeof value.name === "string") {
    return value.name;
  }

  if (value !== null && typeof value === "object") {
    if (parentKey === "fans" && "type" in value) {
      const type = value.type as DuctType;
      return type === DuctType.SUPPLY ? m.air_handler_unit_fan_supply() : m.air_handler_unit_fan_return();
    }

    if (parentKey === "dampers") {
      const type = "type" in value ? (value.type as DuctType | undefined) : undefined;
      if (type) return DUCT_TYPE_TITLE[type]();
      return m.air_handler_unit_damper_recirculation();
    }

    if (parentKey === "coils" && "coilType" in value) {
      return COIL_TITLE[value.coilType as CoilType]();
    }

    if (parentKey === "filters" && "type" in value) {
      return getFilterLabel(value.type as DuctType, "kind" in value ? (value.kind as FilterKind) : FilterKind.MAIN);
    }

    if (parentKey === "flows" && "type" in value) {
      return DUCT_TYPE_TITLE[value.type as DuctType]();
    }
  }

  return Number.isFinite(Number(key)) ? String(Number(key) + 1) : getKeyLabel(key);
};
